import { resolveLayout } from '@usespaceui/squishmoji';
import {
  ACTIVITY_SHAPE_MORPH_MS,
  ACTIVITY_SHAPE_SEQUENCE,
} from '@/components/chat/agent-activity-sequence';

export type VoiceAvatarMode = 'listening' | 'speaking' | 'idle';
export type VoiceColor = readonly [
  lightness: number,
  chroma: number,
  hue: number,
];
export const VOICE_BASE_COLOR: VoiceColor = [0.69, 0.14, 281.4869466171821];
const SHADE_OFFSETS: readonly (readonly [number, number])[] = [
  [0, 0],
  [-16, -6],
  [12, 6],
  [-10, -3],
  [8, 4],
  [-14, -5],
  [16, 5],
  [-8, -1],
  [10, 3],
  [-12, -4],
  [14, 6],
  [-6, -2],
  [4, 2],
  [-16, -5],
  [16, 4],
  [-10, -6],
  [12, 5],
  [-14, -3],
  [8, 6],
  [-8, -4],
  [6, 3],
];
const HOLD_MS = ACTIVITY_SHAPE_SEQUENCE[0]!.holdMs;
const CYCLE_MS = ACTIVITY_SHAPE_MORPH_MS + HOLD_MS;
/** Listening has shown every shade once by this time. */
export const VOICE_LISTENING_SPAN_MS =
  HOLD_MS + SHADE_OFFSETS.length * CYCLE_MS;
/** One speaking pulse. */
export const VOICE_SPEAKING_SPAN_MS = 850;
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));
const elapsed = (time: number) =>
  Number.isFinite(time) ? Math.max(0, time) : 0;

/** Color interpolation uses the same ease-out-quart curve as SquishEngine. */
export function getListeningFrame(timeMs: number) {
  const time = elapsed(timeMs);
  if (time < HOLD_MS) return { index: 0, previous: 0, morphAt: 0, progress: 1 };
  const step = Math.floor((time - HOLD_MS) / CYCLE_MS) + 1;
  const index = step % ACTIVITY_SHAPE_SEQUENCE.length;
  const morphAt = HOLD_MS + (step - 1) * CYCLE_MS;
  const progress = clamp((time - morphAt) / ACTIVITY_SHAPE_MORPH_MS, 0, 1);
  return {
    index,
    previous:
      (index + ACTIVITY_SHAPE_SEQUENCE.length - 1) %
      ACTIVITY_SHAPE_SEQUENCE.length,
    morphAt,
    progress: 1 - (1 - progress) ** 4,
  };
}

function listeningShade(base: VoiceColor, index: number): VoiceColor {
  const [chroma, lightness] = SHADE_OFFSETS[index]!;
  return [
    clamp(base[0] + lightness / 100, 0.2, 0.95),
    clamp(base[1] * (1 + chroma * 0.014), 0, 0.4),
    base[2],
  ];
}

export function mixVoiceColor(
  start: VoiceColor,
  end: VoiceColor,
  progress: number
): VoiceColor {
  const amount = Number.isFinite(progress) ? clamp(progress, 0, 1) : 0;
  return [
    start[0] + (end[0] - start[0]) * amount,
    start[1] + (end[1] - start[1]) * amount,
    start[2] + (end[2] - start[2]) * amount,
  ];
}

/**
 * The avatar colour at a moment of a call. Every shade keeps the hue of
 * `base`, the member's Your IMAI colour, so the call reads as their IMAI.
 */
export function getVoiceColor(
  mode: VoiceAvatarMode,
  timeMs: number,
  base: VoiceColor = VOICE_BASE_COLOR
): VoiceColor {
  if (mode === 'idle') return base;
  if (mode === 'listening') {
    const { index, previous, progress } = getListeningFrame(timeMs);
    return mixVoiceColor(
      listeningShade(base, previous),
      listeningShade(base, index),
      progress
    );
  }
  const phase =
    (elapsed(timeMs) % VOICE_SPEAKING_SPAN_MS) / VOICE_SPEAKING_SPAN_MS;
  const progress = (1 - Math.cos(phase * Math.PI * 2)) / 2;
  // Speaking deepens the body; a near-black one lightens instead so the
  // pulse stays visible.
  const direction = base[0] < 0.45 ? -1 : 1;
  return mixVoiceColor(
    [
      clamp(base[0] - direction * 0.2, 0.2, 0.95),
      clamp(base[1] * 1.21, 0, 0.4),
      base[2],
    ],
    [clamp(base[0] - direction * 0.14, 0.2, 0.95), base[1] * 0.86, base[2]],
    progress
  );
}

const baseColorBySeed = new Map<string, VoiceColor>();

/** sRGB hex to OKLCH, the space the voice shades are mixed in. */
export function hexToVoiceColor(hex: string): VoiceColor | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) return null;
  const value = Number.parseInt(match[1], 16);
  const [r, g, b] = [16, 8, 0].map((shift) => {
    const channel = ((value >> shift) & 0xff) / 255;
    return channel <= 0.04045
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bAxis = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const hue = (Math.atan2(bAxis, a) * 180) / Math.PI;
  return [lightness, Math.hypot(a, bAxis), hue < 0 ? hue + 360 : hue];
}

/** The body colour squishmoji gives a Your IMAI colour seed, as OKLCH. */
export function voiceBaseColorForSeed(colorSeed: string): VoiceColor {
  const cached = baseColorBySeed.get(colorSeed);
  if (cached) return cached;
  const color =
    hexToVoiceColor(resolveLayout(colorSeed).colors.body) ?? VOICE_BASE_COLOR;
  baseColorBySeed.set(colorSeed, color);
  return color;
}

/**
 * The call rings and arcs around the body: the persona's hue at a middle
 * lightness, so they stay visible on light and dark pages alike.
 */
export function voiceAccentColor(base: VoiceColor): VoiceColor {
  // A neutral body (black) keeps a neutral grey rather than a stray hue.
  const chroma = base[1] < 0.02 ? 0 : Math.max(base[1], 0.06);
  return [clamp(base[0], 0.56, 0.7), chroma, base[2]];
}

export function voiceGradientColor(
  color: VoiceColor,
  offset: -1 | 0 | 1
): VoiceColor {
  return [clamp(color[0] + offset * 0.012, 0.2, 0.95), color[1], color[2]];
}

export function voiceColorCss(color: VoiceColor) {
  return `oklch(${(color[0] * 100).toFixed(3)}% ${color[1].toFixed(4)} ${color[2].toFixed(2)})`;
}
